import { isStableFarm } from '@pancakeswap/farms'
import { ButtonMenu, ButtonMenuItem, Flex, ArrowBackIcon } from '@pancakeswap/uikit'
import { useCurrency } from 'hooks/Tokens'
import { useRouter } from 'next/router'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { CHAIN_IDS } from 'utils/wagmi'
import { AddLiquidityV3Layout, UniversalAddLiquidity } from 'views/AddLiquidityV3'
import LiquidityFormProvider from 'views/AddLiquidityV3/formViews/V3FormView/form/LiquidityFormProvider'
import { useCurrencyParams } from 'views/AddLiquidityV3/hooks/useCurrencyParams'
import { SELECTOR_TYPE } from 'views/AddLiquidityV3/types'
import { V3SubgraphHealthIndicator } from 'components/SubgraphHealthIndicator'
import AddLiquidityV2FormProvider from 'views/AddLiquidity/AddLiquidityV2FormProvider'
import Page from 'components/Layout/Page'
import EButtonSm from 'components/EButtonSm'
import EPageHeader from 'components/EPageHeader'
import useStableConfig from 'views/Swap/hooks/useStableConfig'
import { Currency } from '@pancakeswap/swap-sdk-core'
import currencyId from 'utils/currencyId'
import { safeGetAddress } from 'utils'
import { useActiveChainId } from 'hooks/useActiveChainId'
import { NATIVE, WNATIVE } from '@pancakeswap/sdk'
import { useStableSwapPairs } from 'state/swap/useStableSwapPairs'
import styled from 'styled-components'
import Link from "next/link"
import { useTranslation } from "@pancakeswap/localization"

const TabItem = styled(EButtonSm)`
  border: none;
  background: none;
  border-radius: 0;
  border-bottom: 3px solid ${({ theme, isActive }) => (isActive ? theme.colors.primary : 'transparent')};
  transition: border-color 0.2s ease;
`

const BackButtonStyle = styled(Link)`
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 16px 0;
    color: ${({ theme }) => theme.colors.textSubtle};
    stroke: ${({ theme }) => theme.colors.textSubtle};
    transition: color 0.2s ease, stroke 0.2s ease;
    &:hover {
        color: ${({ theme }) => theme.colors.white};
        stroke: ${({ theme }) => theme.colors.white};
    }
`

const StyledFlex = styled(Flex)`
  > * + * {
    margin-top: 16px;
    
    ${({ theme }) => theme.mediaQueries.sm} {
      margin-top: 16px;
    }
  }
`

const AddLiquidityPage = () => {
  const { t } = useTranslation()
  const router = useRouter()
  const { chainId } = useActiveChainId()

  const { currencyIdA, currencyIdB, feeAmount } = useCurrencyParams()
  const baseCurrency = useCurrency(currencyIdA)
  const currencyB = useCurrency(currencyIdB)

  const stablePairs = useStableSwapPairs()
  const stableConfig = useStableConfig({
    tokenA: baseCurrency as Currency,
    tokenB: currencyB as Currency,
  })

  const [selectorType, setSelectorType] = useState(SELECTOR_TYPE.V2)

  const isStable = !!stableConfig.stableSwapConfig
  const isV2 = router.query.v2 === 'true'

  useEffect(() => {
    if (router.query.stable === 'true') {
      setSelectorType(SELECTOR_TYPE.STABLE)
    } else if (router.query.v2 === 'true') {
      setSelectorType(SELECTOR_TYPE.V2)
    } else {
      setSelectorType(SELECTOR_TYPE.V3)
    }
  }, [router.query])

  const handleRefresh = useCallback(() => {
    router.replace(
      {
        pathname: router.pathname,
        query: {
          currency: [currencyIdA, currencyIdB],
        },
      },
      undefined,
      { shallow: true },
    )
  }, [router, currencyIdA, currencyIdB])

  const handleStable = useCallback(() => {
    if (!stableConfig.stableSwapConfig) {
      if (stablePairs && stablePairs.length > 0) {
        const idA = currencyId(stablePairs[0].token0)
        const idB = currencyId(stablePairs[0].token1)
        if (!idA || !idB) return
        setSelectorType(SELECTOR_TYPE.STABLE)
        router.replace(
          {
            pathname: router.pathname,
            query: {
              currency: [idA, idB],
            },
          },
          undefined,
          { shallow: true },
        )
      }
    }
  }, [router, stableConfig, stablePairs, setSelectorType])

  const handleSelector = useCallback(
    (_selectorType) => {
      if (stableConfig.stableSwapConfig && chainId) {
        const idA = NATIVE[chainId]
        router.replace(
          {
            pathname: router.pathname,
            query: {
              currency: [idA.symbol, currencyB?.symbol],
              ...((_selectorType === SELECTOR_TYPE.V2) ? { v2: 'true' } : {}),
              ...((_selectorType === SELECTOR_TYPE.STABLE) ? { stable: 'true' } : {}),
            },
          },
          undefined,
          { shallow: true },
        )
      }
      setSelectorType(_selectorType)
    },
    [router, stableConfig, chainId, currencyB, setSelectorType],
  )

  useEffect(() => {
    if (!currencyIdA || !currencyIdB) return

    if (stableConfig.stableSwapConfig) {
      handleStable()
    } else if (selectorType === SELECTOR_TYPE.STABLE) {
      handleSelector(SELECTOR_TYPE.V3)
    }
  }, [currencyIdA, currencyIdB, handleSelector, handleStable, stableConfig])

  const backUrl = useMemo(() => {
    return router.query.stable === 'true' ? '/liquidity/stable' : '/liquidity/v2'
  }, [router.query.stable])

  const pageTitle = useMemo(() => {
    switch (selectorType) {
      case SELECTOR_TYPE.V2:
        return t('Liquidity V2')
      case SELECTOR_TYPE.STABLE:
        return t('Stable Liquidity')
      case SELECTOR_TYPE.V3:
        return t('Liquidity V3')
      default:
        return t('Add Liquidity')
    }
  }, [selectorType, t])

  return (
    <Page>
      <StyledFlex
        flexDirection="column"
        maxWidth={750}
        width="100%"
        height="100%"
        position="relative"
        alignItems="center"
        margin="0 auto"
      >
        <EPageHeader pageName={pageTitle} />
        <BackButtonStyle href={backUrl}>
          <ArrowBackIcon height="16px" />
          {t('Back')}
        </BackButtonStyle>

        <AddLiquidityV2FormProvider>
          <LiquidityFormProvider>
            <AddLiquidityV3Layout handleRefresh={handleRefresh} selectorType={selectorType}>
              <UniversalAddLiquidity 
                selectorType={selectorType} 
                currencyIdA={currencyIdA} 
                currencyIdB={currencyIdB} 
                isV2={isV2}
              />
              <V3SubgraphHealthIndicator />
            </AddLiquidityV3Layout>
          </LiquidityFormProvider>
        </AddLiquidityV2FormProvider>
      </StyledFlex>
    </Page>
  )
}

AddLiquidityPage.chains = CHAIN_IDS

export default AddLiquidityPage
